import {
  Target, Users, UserPlus, Briefcase, Presentation, Crown, Globe2, PenLine,
  Phone, FileText, Receipt, Factory, Handshake, Package, Send, UserCheck, Coffee, Link2, type LucideIcon,
} from "lucide-react";

export type GoalKey = "sell" | "partners" | "hiring" | "job_search" | "event" | "investors" | "networking" | "other";
export type QKind = "product" | "single" | "multi" | "chips" | "who" | "text" | "where" | "cta" | "skip";

export interface Option { v: string; label: string; help?: string; icon?: LucideIcon; link?: { label: string; placeholder: string }; phone?: boolean }
export interface Question {
  id: string;
  kind: QKind;
  title: string;
  help?: string;
  /** Short label used in the live summary, e.g. "Sell", "To", "Where". */
  summary: string;
  options?: Option[];
  placeholder?: string;
  suggestions?: string[];
  optional?: boolean;
  /** For "text": an optional link field shown under the text box. */
  link?: { label: string; placeholder: string };
  /** For "who": also ask company size on the same screen (saved as `sizes`). */
  withSizes?: boolean;
}
export interface Goal { v: GoalKey; label: string; help: string; icon: LucideIcon; primary?: boolean }
export interface FlowContext {
  products: { name: string; description?: string }[];
  industries: string[];
  countries: string[];
  lastAsk?: { first_ask?: string | null; ask_details?: { link?: string; phone?: string } | null } | null;
}
export type Answers = Record<string, string | string[] | undefined>;

export const GOALS: Goal[] = [
  { v: "sell", label: "Win clients", help: "Sell a product or service", icon: Target, primary: true },
  { v: "partners", label: "Find partners", help: "Distributors, dealers, resellers", icon: Handshake, primary: true },
  { v: "hiring", label: "Hire people", help: "Reach candidates for a role", icon: UserPlus, primary: true },
  { v: "job_search", label: "Find a job", help: "Reach hiring managers", icon: Briefcase },
  { v: "event", label: "Promote event", help: "Fill seats for an event or webinar", icon: Presentation, primary: true },
  { v: "investors", label: "Raise funding", help: "Reach VCs and angels", icon: Crown },
  { v: "networking", label: "Grow network", help: "Meet peers and leaders", icon: Globe2 },
  { v: "other", label: "Something else", help: "Describe it in a line", icon: PenLine },
];

const BOOKING = { label: "Booking link", placeholder: "https://calendly.com/you" };

export const SIZE_OPTIONS: Option[] = [
  { v: "1-10", label: "1–10", help: "Very small" },
  { v: "11-50", label: "11–50", help: "Small" },
  { v: "51-200", label: "51–200", help: "Mid-size" },
  { v: "201-500", label: "201–500", help: "Large" },
  { v: "501-1000", label: "501–1,000", help: "Very large" },
  { v: "1000+", label: "1,000+", help: "Enterprise" },
];

const where = (title = "Where are your buyers?", help = "Pick a market, then fine-tune the countries."): Question =>
  ({ id: "countries", kind: "where", title, help, summary: "Where" });

export const FLOWS: Record<GoalKey, Question[]> = {
  sell: [
    { id: "product", kind: "product", title: "What are we selling?", help: "One product keeps every message sharp.", summary: "Sell" },
    { id: "industries", kind: "who", title: "Which industries?", summary: "Industries", placeholder: "Add an industry", withSizes: true },
    where(),
    { id: "cta", kind: "cta", title: "What should prospects do first?", help: "Your messages will steer towards this.", summary: "Next step", options: [
      { v: "A short call", label: "A short call", help: "15–20 minutes to discuss needs", icon: Phone, link: BOOKING, phone: true },
      { v: "Send our catalogue / brochure", label: "Send the catalogue", help: "Share your brochure or price list", icon: FileText, link: { label: "Catalogue link", placeholder: "https://…/catalogue.pdf" } },
      { v: "Share a quote", label: "Get a quote", help: "They share needs, you send pricing", icon: Receipt },
      { v: "Book a demo", label: "Book a demo", help: "Show the product live", icon: Presentation, link: BOOKING },
      { v: "Visit our plant / office", label: "Visit us", help: "Invite them to your plant or office", icon: Factory },
    ] },
  ],
  partners: [
    { id: "product", kind: "product", title: "What should partners sell?", help: "One product per campaign.", summary: "Product" },
    { id: "partner_type", kind: "multi", title: "Which kind of partner?", summary: "Partners", options: [
      { v: "Distributors", label: "Distributors", help: "Stock and supply in a region", icon: Package },
      { v: "Dealers", label: "Dealers", help: "Sell directly to end customers", icon: Users },
      { v: "Resellers", label: "Resellers", help: "Resell under their own brand", icon: Send },
      { v: "Agencies / system integrators", label: "Agencies & integrators", help: "Bundle it into their services", icon: Handshake },
    ] },
    where("Where do you need partners?"),
    { id: "cta", kind: "cta", title: "What should partners do first?", summary: "Next step", options: [
      { v: "A short partnership call", label: "A partnership call", help: "Discuss margins and territory", icon: Phone, link: BOOKING, phone: true },
      { v: "Send our partner kit", label: "Send the partner kit", help: "Share terms and product details", icon: FileText, link: { label: "Partner kit link", placeholder: "https://…" } },
    ] },
  ],
  hiring: [
    { id: "role", kind: "text", title: "Which role are you hiring?", help: "A job link helps us get the details right.", summary: "Role", placeholder: "e.g. Senior React Developer", link: { label: "Job link (optional)", placeholder: "https://careers.yourcompany.com/…" } },
    { id: "experience", kind: "single", title: "How experienced?", summary: "Experience", options: [
      { v: "0–2 years", label: "Early career", help: "0–2 years" },
      { v: "3–6 years", label: "Mid-level", help: "3–6 years" },
      { v: "7–12 years", label: "Senior", help: "7–12 years" },
      { v: "12+ years", label: "Leadership", help: "12+ years" },
    ] },
    where("Where are the candidates?"),
    { id: "cta", kind: "cta", title: "What should candidates do first?", summary: "Next step", options: [
      { v: "Apply via link", label: "Apply", help: "Send them to the application", icon: Link2, link: { label: "Application link", placeholder: "https://…" } },
      { v: "A short chat about the role", label: "A quick chat", help: "10–15 minutes with you", icon: Phone, link: BOOKING, phone: true },
      { v: "Share the job description", label: "Get the job details", help: "Share the full description", icon: FileText, link: { label: "Job description link", placeholder: "https://…" } },
    ] },
  ],
  job_search: [
    { id: "role", kind: "text", title: "What role do you want next?", summary: "Role", placeholder: "e.g. Product Manager" },
    { id: "industries", kind: "chips", title: "Which industries?", help: "We'll reach hiring managers there.", summary: "Industries", placeholder: "e.g. Fintech, SaaS" },
    where("Where do you want to work?"),
    { id: "cta", kind: "cta", title: "What do you want from them?", summary: "Ask", options: [
      { v: "A referral", label: "A referral", help: "Refer you for an open role", icon: UserCheck },
      { v: "A quick chat", label: "A quick chat", help: "10 minutes to learn about the team", icon: Coffee, link: BOOKING },
      { v: "Review my CV", label: "Look at my CV", help: "Share your CV or portfolio", icon: FileText, link: { label: "CV / portfolio link", placeholder: "https://…" } },
    ] },
  ],
  event: [
    { id: "event", kind: "text", title: "Which event?", help: "Name, date and city, or paste the event page.", summary: "Event", placeholder: "e.g. Pack Expo, 12 March, Mumbai", link: { label: "Event link (optional)", placeholder: "https://…" } },
    { id: "attendees", kind: "chips", title: "Who should attend?", help: "Job titles of your ideal guests.", summary: "Invite", placeholder: "e.g. Plant Head", suggestions: ["Founder", "CEO", "Plant Head", "Head of Operations", "Purchase Manager", "CTO", "Marketing Head"] },
    where("Where are your guests?"),
    { id: "cta", kind: "cta", title: "What should guests do?", summary: "Next step", options: [
      { v: "Register for the event", label: "Register", help: "Sign up through your link", icon: Presentation, link: { label: "Registration link", placeholder: "https://…" } },
      { v: "Meet us at the event / booth", label: "Meet at the booth", help: "Fix a time to meet in person", icon: Users },
    ] },
  ],
  investors: [
    { id: "stage", kind: "single", title: "Which round are you raising?", summary: "Round", options: [
      { v: "Pre-seed", label: "Pre-seed", help: "Idea or early product" },
      { v: "Seed", label: "Seed", help: "Product with early traction" },
      { v: "Series A", label: "Series A", help: "Proven growth" },
      { v: "Series B+", label: "Series B or later", help: "Scaling up" },
    ] },
    { id: "amount", kind: "text", title: "How much?", help: "Optional, but it helps investors self-select.", summary: "Amount", placeholder: "e.g. ₹5 crore / $1M", optional: true },
    { id: "investor_type", kind: "multi", title: "Which investors?", summary: "Investors", options: [
      { v: "Venture capital firms", label: "VC firms", icon: Crown },
      { v: "Angel investors", label: "Angels", icon: UserCheck },
      { v: "Family offices", label: "Family offices", icon: Users },
      { v: "Corporate VCs", label: "Corporate VCs", icon: Briefcase },
    ] },
    where("Where are the investors?"),
    { id: "cta", kind: "cta", title: "What should investors do first?", summary: "Next step", options: [
      { v: "A short intro call", label: "An intro call", help: "20 minutes with the founders", icon: Phone, link: BOOKING },
      { v: "Share our pitch deck", label: "See the deck", help: "Share a deck link", icon: FileText, link: { label: "Pitch deck link", placeholder: "https://docsend.com/…" } },
    ] },
  ],
  networking: [
    { id: "who", kind: "chips", title: "Who do you want to meet?", help: "Job titles of the people you'd like to know.", summary: "Meet", placeholder: "e.g. CFO", suggestions: ["Founder", "CEO", "CFO", "CTO", "Head of Sales", "HR Head", "Investor"] },
    { id: "why", kind: "single", title: "Why connect?", summary: "Why", options: [
      { v: "Learn from peers", label: "Learn from peers", icon: Users },
      { v: "Find partnership opportunities", label: "Find partnerships", icon: Handshake },
      { v: "Build visibility for my brand", label: "Build visibility", icon: Globe2 },
      { v: "Explore career opportunities", label: "Explore opportunities", icon: Briefcase },
    ] },
    where("Where are these people?"),
    { id: "cta", kind: "cta", title: "What's the first step?", summary: "Next step", options: [
      { v: "Connect and stay in touch", label: "Just connect", help: "Start a relationship, no ask", icon: UserPlus },
      { v: "A virtual coffee", label: "A virtual coffee", help: "15 minutes to get to know each other", icon: Coffee, link: BOOKING },
    ] },
  ],
  other: [
    { id: "describe", kind: "text", title: "What should this campaign achieve?", summary: "Goal", placeholder: "e.g. Get feedback on our new app from HR heads" },
    { id: "who", kind: "chips", title: "Who should we reach?", help: "Job titles.", summary: "Reach", placeholder: "e.g. HR Head" },
    where(),
    { id: "cta", kind: "cta", title: "What should they do next?", summary: "Next step", options: [
      { v: "A short call", label: "A short call", help: "15–20 minutes", icon: Phone, link: BOOKING, phone: true },
      { v: "Reply with feedback", label: "Reply to the message", help: "Answer by message", icon: Send },
    ] },
  ],
};

export const SKIP_SUGGESTIONS = ["Students", "Recruiters", "Freelancers", "Job seekers", "Competitors", "Interns"];
export const SKIP_Q: Question = { id: "skip", kind: "skip", title: "Anyone to skip?", summary: "Skip", optional: true, placeholder: "Company name or word" };

/** The questions actually shown: the product step is skipped when there is exactly one product. */
export function stepsFor(goal: GoalKey, ctx: Pick<FlowContext, "products">): Question[] {
  return [...FLOWS[goal].filter((q) => !(q.kind === "product" && ctx.products.length === 1)), SKIP_Q];
}

/** Short, one-line product label: "CRM Software - suite for SMEs" -> "CRM Software". */
export function shortProductName(name: string, max = 28): string {
  let s = String(name ?? "").trim();
  const cut = s.split(/\s[-–—]\s|[:(|,]/)[0].trim();
  if (cut) s = cut;
  if (s.length <= max) return s;
  const words = s.slice(0, max).split(" ");
  if (words.length > 1) words.pop();
  return words.join(" ").replace(/[\s.,;-]+$/, "") + "…";
}

export const PRODUCT_GOALS: GoalKey[] = ["sell", "partners"];

const filled = (v: Answers[string]) => (Array.isArray(v) ? v.length > 0 : !!String(v ?? "").trim());
export function isAnswered(q: Question, a: Answers): boolean {
  if (q.optional) return true;
  if (q.kind === "where") return filled(a.countries);
  return filled(a[q.id]);
}

export const INDIA_PLACES = ["India", "Mumbai", "Pune", "Bengaluru", "Bangalore", "Delhi NCR", "Delhi", "Chennai", "Hyderabad", "Ahmedabad", "Kolkata", "Gurgaon", "Noida", "Jaipur", "Surat", "Indore", "Chandigarh", "Kochi", "Coimbatore", "Vadodara", "Lucknow", "Nagpur"];
export const isIndianPlace = (c: string) => INDIA_PLACES.some((x) => x.toLowerCase() === c.trim().toLowerCase());

/** One market only: India (+cities) or one abroad country. */
export function normalizePlaces(list: string[]): { market: "india" | "abroad"; countries: string[] } {
  const clean = list.map((c) => c.trim()).filter(Boolean);
  if (!clean.length || isIndianPlace(clean[0])) {
    const india = clean.filter(isIndianPlace);
    return { market: "india", countries: india.length ? india : ["India"] };
  }
  return { market: "abroad", countries: [clean[0]] };
}

/** Pick a single abroad country (replaces any previous country and its cities). */
export function pickAbroadCountry(current: string[], country: string): string[] {
  return current[0]?.toLowerCase() === country.toLowerCase() ? [] : [country];
}

/** Set cities for the chosen abroad country; empty list means "All of <country>". */
export function setAbroadCities(current: string[], cities: string[]): string[] {
  return current[0] ? [current[0], ...cities.filter((c) => c.toLowerCase() !== current[0].toLowerCase())] : [];
}

/** Starting answers from the business profile and the last campaign. */
export function prefill(goal: GoalKey, ctx: FlowContext): Answers {
  const w = normalizePlaces(ctx.countries);
  const a: Answers = { countries: w.countries, market: w.market };
  const qs = FLOWS[goal];
  if (qs.some((q) => q.id === "product") && ctx.products.length === 1) a.product = ctx.products[0].name;
  if (goal === "sell" && ctx.industries.length) a.industries = ctx.industries.slice(0, 6);
  const cta = qs.find((q) => q.kind === "cta");
  const last = ctx.lastAsk?.first_ask;
  if (cta && last && cta.options?.some((o) => o.v === last)) {
    a.cta = last;
    a.cta_link = ctx.lastAsk?.ask_details?.link ?? "";
    a.cta_phone = ctx.lastAsk?.ask_details?.phone ?? "";
  }
  return a;
}

/** Map answers to the existing suggest + save payloads. */
export function toRequest(goal: GoalKey, a: Answers) {
  const qs = FLOWS[goal];
  const str = (v: Answers[string]) => (Array.isArray(v) ? v.join(", ") : String(v ?? "").trim());
  const details = qs
    .filter((q) => !["product", "where", "cta", "sizes"].includes(q.kind) && !(goal === "sell" && q.id === "industries") && filled(a[q.id]))
    .map((q) => `${q.summary}: ${str(a[q.id])}`);
  const link = String(a[`${qs.find((q) => q.link)?.id ?? "_"}_link`] ?? "").trim();
  if (link) details.push(`Link: ${link}`);
  const industries = qs.some((q) => q.id === "industries") ? ((a.industries as string[]) ?? []) : [];
  const sizes = qs.some((q) => q.id === "sizes" || q.withSizes) ? ((a.sizes as string[]) ?? []) : [];
  const countries = (a.countries as string[]) ?? [];
  const product = qs.some((q) => q.kind === "product") ? String(a.product ?? "") : "";
  const goalFields: Record<string, string | string[]> = {};
  for (const q of qs) if (filled(a[q.id])) goalFields[q.id] = a[q.id] as string | string[];
  if (sizes.length && !goalFields.sizes) goalFields.sizes = sizes;
  return {
    goal,
    goalCustom: goal === "other" ? str(a.describe) : "",
    goalDetails: details.join("; ").slice(0, 1200),
    goalFields,
    sourceUrl: /^https?:\/\/\S+$/i.test(link) ? link : undefined,
    products: product ? [product] : [],
    industries,
    companySizes: sizes,
    countries,
    firstAsk: String(a.cta ?? ""),
    askDetails: { link: String(a.cta_link ?? "").trim(), phone: String(a.cta_phone ?? "").trim(), duration: "", location: "", quote_info: "" },
    doNotContact: ((a.skip as string[]) ?? []).join(", "),
  };
}

/** Rebuild wizard answers from a saved v2 campaign profile. Returns null when it can't be shown in the wizard. */
export function fromSaved(v2: any): { goal: GoalKey; answers: Answers } | null {
  const s = v2?.suggestion;
  if (!s) return null;
  const c = v2?.confirmed ?? {};
  const raw = c.goal === "agency" ? "sell" : c.goal;
  const goal: GoalKey = GOALS.some((g) => g.v === raw) ? raw : "sell";
  const qs = FLOWS[goal];
  const a: Answers = {};
  const gf = c.goal_fields && typeof c.goal_fields === "object" ? c.goal_fields : {};
  for (const q of qs) if (gf[q.id] !== undefined && q.kind !== "where" && q.kind !== "cta") a[q.id] = gf[q.id];
  if (qs.some((q) => q.kind === "product")) a.product = (Array.isArray(c.products) && c.products[0]) || s.product || "";
  if (qs.some((q) => q.id === "industries") && !filled(a.industries)) {
    const inds = v2.buyer_industries ?? s.buyer_industries;
    if (Array.isArray(inds)) a.industries = inds.map(String);
  }
  const sizes = Array.isArray(c.company_sizes) ? c.company_sizes : c.company_size ? String(c.company_size).split(/,\s*/).filter(Boolean) : [];
  if (sizes.length) a.sizes = sizes;
  const saved: string[] = Array.isArray(c.countries) && c.countries.length ? c.countries : String(c.region ?? s.region ?? "").split(/,\s*/).filter(Boolean);
  if (saved.length && isIndianPlace(saved[0])) a.countries = saved.filter(isIndianPlace), a.market = "india";
  else if (saved.length) a.countries = saved, a.market = "abroad";
  else Object.assign(a, normalizePlaces([]));
  if (c.first_ask) a.cta = c.first_ask;
  a.cta_link = c.ask_details?.link ?? "";
  a.cta_phone = c.ask_details?.phone ?? "";
  const skip = String(c.do_not_contact ?? v2.do_not_contact ?? "").split(/,\s*/).map((x) => x.trim()).filter(Boolean);
  if (skip.length) a.skip = skip;
  if (goal === "other" && c.goal_custom && !a.describe) a.describe = c.goal_custom;
  return { goal, answers: a };
}
